// ============================================================
// 📁 آدرس این فایل در پروژه:
//    src/pages/finance/journal/Purchase.tsx
//
// تغییرات این نسخه:
// - 🆕 صفحه‌بندی سمت سرور (۵۰ تایی) + فیلتر پیشرفته (فروشنده/تاریخ/جنس/کلیدواژه)
// - 🆕 نوع خرید: داخلی / خارجی (خارجی = بدون گدام، بدون اثر روی موجودی، در انتظار ترانزیت)
// - 🆕 فیلد جنس و فیلد گدام کاملاً جدا و مستقل (هر دو تایپی/جستجویی)
// - 🆕 ضمیمه: آپلود واقعی فایل (انتخاب از کامپیوتر یا Paste) به‌جای تایپ لینک
// - 🆕 پرسش «آیا تراکنش کرایه موتر ثبت شود؟» قبل از ثبت نهایی
// - 🆕 پیش‌نمایش شیک قبل از ثبت نهایی + چاپ/ذخیره PDF
// - بقیه‌ی منطق (هشدار کسری، ویرایش، تأیید/برگشت در انتظار) دست نخورده
// ============================================================

import { useState, useEffect, useCallback, useRef } from "react";
import { useTheme, DARK, LIGHT } from "../../../components/Layout";
import { journalAPI, vendorsAPI, warehouseAPI, trackingAPI } from "../../../services/api";
import { usePermission } from "../../../hooks/usePermission";
import { useSubmit } from "../../../hooks/useSubmit";
import SearchableSelect from "../../../components/SearchableSelect";
import AttachmentField from "../../../components/AttachmentField";
import PersianDatePicker, { todayGregorian, gregorianToJalaliStr, DualDate } from "../../../utils/PersianDatePicker";
import { Plus, Trash2, X, Printer, Eye, Edit2, AlertTriangle, Paperclip, Truck, Ship } from "lucide-react";
import JournalFilter, { emptyFilter, JournalFilterValues } from "./JournalFilter";
import TxnReportModal from "./TxnReportModal";
import { describeFilter } from "./useTxnList";
import { fileUrl } from "../../../utils/txnReport";
import {
  StatusTabs, PendingBadge, ConfirmIconButton, RevertIconButton,
  PendingSubmitButton, ConfirmSubmitButton, PendingHint, pendingRowStyle,
} from "./PendingControls";

const fmt = (n: any) =>
  new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);

const PAGE_SIZE = 50;

interface Item {
  product_id: string; product_name: string; unit: string;
  quantity: number; warehouse_id: string; warehouse_name: string;
  unit_price: number; total_price: number;
  product_category_name?: string;
}

// 🆕 گزینه‌ی محصول/خدمت — مستقل از گدام
interface ProductOnlyOption {
  value: string;         // "42" برای جنس، "svc_7" برای خدمت
  label: string;
  product_id: number;
  product_name: string;
  unit: string;
  item_type: "product" | "service";
  category_name?: string;
}

// 🆕 ساختار کسری شناسایی شده
interface DetectedShortage {
  shipment_item_id: number;
  product_id: number;
  product_name: string;
  original_qty: number;
  actual_qty: number;
  shortage_qty: number;
}

export default function Purchase() {
  const { dark } = useTheme();
  const T = dark ? DARK : LIGHT;
  const canAdd    = usePermission("finance", "add");
  const canEdit   = usePermission("finance", "edit");
  const canDelete = usePermission("finance", "delete");
  const { submitting, submit } = useSubmit();

  const [list, setList]             = useState<any[]>([]);
  const [loading, setLoading]       = useState(true);
  const [showForm, setShowForm]     = useState(false);
  const [editItem, setEditItem]     = useState<any>(null);
  const [viewItem, setViewItem]     = useState<any>(null);
  const [vendors, setVendors]       = useState<any[]>([]);

  // 🆕 گزینه‌های جنس و گدام کاملاً جدا از هم
  const [productOnlyOptions, setProductOnlyOptions] = useState<ProductOnlyOption[]>([]);

  const [filter, setFilter]         = useState<JournalFilterValues>(emptyFilter);
  const [toast, setToast]           = useState({ text: "", ok: true });
  const [statusFilter, setStatusFilter] = useState<"all"|"pending"|"confirmed">("all");

  // 🆕 صفحه‌بندی سمت سرور
  const [page, setPage]             = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalAll, setTotalAll]     = useState(0);
  const [pendingCount, setPendingCount]     = useState(0);
  const [confirmedCount, setConfirmedCount] = useState(0);

  // 🆕 state برای مدیریت هشدار کسری
  const [shortageWarning, setShortageWarning] = useState<{
    shortages: DetectedShortage[];
    shipmentInfo: { id: number; serial: string };
    purchaseId: number;
  } | null>(null);
  const [shortageDescription, setShortageDescription] = useState("");

  const [form, setForm] = useState({
    date: todayGregorian(), vendor_id: "", vendor_currency: "USD",
    discount: "0", notes: "", attachment: "", attachment_file_name: "",
    purchase_type: "domestic" as "domestic" | "foreign", // 🆕
  });
  const [items, setItems] = useState<Item[]>([]);

  // 🆕 پیش‌نمایش قبل از ثبت نهایی (پرسش کرایه موتر از این به بعد در «توزیع بار» است، نه اینجا)
  const [showPreview, setShowPreview] = useState(false);
  const [showReport, setShowReport] = useState(false);

  // ---------- بارگذاری داده‌های ثابت (فروشنده‌ها، جنس‌ها) ----------
  const loadMeta = useCallback(async () => {
    const [v, pr, u, sv] = await Promise.all([
      vendorsAPI.list(),
      warehouseAPI.productsList(),
      warehouseAPI.unitsList(),
      warehouseAPI.servicesList(),
    ]);

    if (v.success) setVendors(v.vendors || []);

    const prodOpts: ProductOnlyOption[] = [];

    if (pr.success) {
      const products = pr.products || [];
      const units = u.success ? (u.units || []) : [];
      products.forEach((prod: any) => {
        const unit = units.find((uu: any) => uu.id === prod.unit_id)?.name || "";
        prodOpts.push({
          value: String(prod.id),
          label: `📦 ${prod.name}`,
          product_id: prod.id,
          product_name: prod.name,
          unit,
          item_type: "product",
          category_name: prod.category_name || "",
        });
      });
    }

    if (sv.success) {
      const services = sv.services || [];
      services.forEach((svc: any) => {
        prodOpts.push({
          value: `svc_${svc.id}`,
          label: `🔧 ${svc.name}`,
          product_id: svc.id,
          product_name: svc.name,
          unit: "",
          item_type: "service",
          category_name: svc.category_name || "",
        });
      });
    }


    setProductOnlyOptions(prodOpts);
  }, []);

  // ---------- بارگذاری لیست خریدها (صفحه‌بندی‌شده + فیلترشده — سمت سرور) ----------
  const loadList = useCallback(async () => {
    setLoading(true);
    const res = await journalAPI.listPurchases({
      page,
      page_size: PAGE_SIZE,
      status: statusFilter,
      vendor_id: filter.counterparty || undefined,
      date_from: filter.dateFrom || undefined,
      date_to: filter.dateTo || undefined,
      keyword: filter.search || undefined,
      product_keyword: filter.productName || undefined,
    });
    if (res.success) {
      setList(res.purchases || []);
      setTotalPages(res.total_pages || 1);
      setTotalAll(res.total_all ?? res.total ?? 0);
      setPendingCount(res.pending_count || 0);
      setConfirmedCount(res.confirmed_count || 0);
    }
    setLoading(false);
  }, [page, statusFilter, filter.counterparty, filter.dateFrom, filter.dateTo, filter.search, filter.productName]);

  useEffect(() => { loadMeta(); }, [loadMeta]);

  // فیلترهای متنی را کمی debounce می‌کنیم تا هر ضربه کلید یک درخواست جدا نسازد
  useEffect(() => {
    const t = setTimeout(() => { loadList(); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter, filter.counterparty, filter.dateFrom, filter.dateTo, filter.search, filter.productName]);

  // وقتی فیلتر تغییر کرد، برگرد به صفحه‌ی اول
  useEffect(() => { setPage(1); }, [statusFilter, filter.counterparty, filter.dateFrom, filter.dateTo, filter.search, filter.productName]);

  const showToast = (text: string, ok = true) => {
    setToast({ text, ok });
    setTimeout(() => setToast({ text: "", ok: true }), 3000);
  };

  const addItem = () =>
    setItems(p => [...p, { product_id: "", product_name: "", unit: "", quantity: 1, warehouse_id: "", warehouse_name: "", unit_price: 0, total_price: 0 }]);

  const removeItem = (i: number) => setItems(p => p.filter((_, idx) => idx !== i));

  // 🆕 انتخاب جنس/خدمت — فقط نام و واحد را ست می‌کند (گدام کاملاً جدا انتخاب می‌شود)
  const updateItemProduct = (i: number, val: string) => {
    const opt = productOnlyOptions.find(o => o.value === val);
    setItems(p => p.map((item, idx) => {
      if (idx !== i) return item;
      const u = { ...item, product_id: val };
      if (opt) {
        u.product_name = opt.product_name;
        u.unit = opt.unit;
      }
      return u;
    }));
  };

  const updateItem = (i: number, key: "quantity" | "unit_price", val: any) => {
    setItems(p => p.map((item, idx) => {
      if (idx !== i) return item;
      const u = { ...item, [key]: val };
      const q = key === "quantity" ? Number(val) : u.quantity;
      const price = key === "unit_price" ? Number(val) : u.unit_price;
      u.total_price = q * price;
      return u;
    }));
  };

  const isForeign = form.purchase_type === "foreign";

  const totalAmount = items.reduce((s, i) => s + i.total_price, 0);
  const netAmount   = totalAmount - (parseFloat(form.discount || "0") || 0);

  const selectedVendor = vendors.find((v: any) => String(v.id) === form.vendor_id);

  const buildSubmitPayload = (statusValue: "pending"|"confirmed") => {
    const vendor = vendors.find((v: any) => String(v.id) === form.vendor_id);
    return {
      date: form.date,
      vendor_id: parseInt(form.vendor_id),
      vendor_name: `${vendor?.first_name || ""} ${vendor?.last_name || ""} ${vendor?.company_name || ""}`.trim(),
      vendor_currency: vendor?.currency || form.vendor_currency,
      discount: parseFloat(form.discount || "0"),
      purchase_type: form.purchase_type, // 🆕
      items: items.map(i => {
        const isService = i.product_id.startsWith("svc_");
        const productId = isService
          ? parseInt(i.product_id.replace("svc_", ""))
          : (i.product_id ? parseInt(i.product_id) : null);
        return {
          product_id:     productId,
          product_name:   i.product_name,
          unit:           i.unit,
          quantity:       i.quantity,
          // 🆕 خرید دیگر گدام تعیین نمی‌کند — این کار در «رسید جنس» / «ترانزیت» انجام می‌شود
          warehouse_id:   null,
          warehouse_name: "",
          unit_price:     i.unit_price,
          total_price:    i.total_price,
          item_type:      isService ? "service" : "product",
        };
      }),
      notes: form.notes,
      attachment: form.attachment,
      status: statusValue,
    };
  };

  const validateForm = () => {
    if (!form.vendor_id) { showToast("فروشنده الزامی است", false); return false; }
    if (items.length === 0) { showToast("حداقل یک آیتم اضافه کنید", false); return false; }
    if (items.some(i => !i.product_name || i.quantity <= 0)) { showToast("اطلاعات آیتم‌ها ناقص است", false); return false; }
    return true;
  };

  const resetForm = () => {
    setForm({
      date: todayGregorian(), vendor_id: "", vendor_currency: "USD", discount: "0",
      notes: "", attachment: "", attachment_file_name: "",
      purchase_type: "domestic",
    });
    setItems([]);
  };

  // 🆕 کلیک روی «ثبت خرید» (نهایی) → مستقیم پیش‌نمایش (پرسش کرایه موتر از این به بعد در «توزیع بار» است)
  const openPreview = () => {
    if (!validateForm()) return;
    setShowPreview(true);
  };

  const handleSubmit = async () => {
    const res = await journalAPI.addPurchase(buildSubmitPayload("confirmed"));
    if (res.success) {
      showToast(`خرید ${res.serial} با موفقیت ثبت شد`);
      setShowForm(false);
      setShowPreview(false);
      resetForm();
      loadList();
      loadMeta();
    } else {
      showToast(res.message || "خطا در ثبت خرید", false);
    }
  };

  const handleSubmitPending = async () => {
    if (!validateForm()) return;
    const res = await journalAPI.addPurchase(buildSubmitPayload("pending"));
    if (res.success) {
      showToast(`خرید ${res.serial} در انتظار تأیید ذخیره شد`);
      setShowForm(false);
      resetForm();
      loadList();
    } else {
      showToast(res.message || "خطا در ثبت خرید", false);
    }
  };

  const handleConfirm = async (id: number, serial: string) => {
    if (!window.confirm(`تأیید نهایی خرید ${serial}؟\nموجودی گدام، حساب مصرفی و بیلانس فروشنده به‌روزرسانی می‌شود.`)) return;
    const res = await journalAPI.confirmPurchase(id);
    if (res.success) { showToast("خرید تأیید شد"); loadList(); }
    else showToast(res.message || "خطا", false);
  };

  const handleRevert = async (id: number, serial: string) => {
    if (!window.confirm(`برگرداندن خرید ${serial} به حالت در انتظار؟\nتأثیرات آن لغو می‌شود.`)) return;
    const res = await journalAPI.revertPurchase(id);
    if (res.success) { showToast("به حالت در انتظار برگشت"); loadList(); }
    else showToast(res.message || "خطا", false);
  };

  const openEdit = (p: any) => {
    setEditItem({
      id: p.id,
      date: p.date,
      vendor_id: String(p.vendor_id),
      vendor_currency: p.vendor_currency,
      discount: String(p.discount || "0"),
      notes: p.notes || "",
      attachment: p.attachment || "",
      status: p.status || "confirmed",
      purchase_type: p.purchase_type || "domestic",
      items: (p.items || []).map((it: any) => ({
        product_id: it.item_type === "service" ? `svc_${it.product_id}` : String(it.product_id),
        product_name: it.product_name,
        unit: it.unit,
        quantity: Number(it.quantity),
        warehouse_id: it.warehouse_id ? String(it.warehouse_id) : "",
        warehouse_name: it.warehouse_name,
        unit_price: Number(it.unit_price),
        total_price: Number(it.total_price),
      })),
    });
  };

  // 🆕 تابع: تشخیص کسری در ویرایش (اگر این خرید به یک بار مرتبط است)
  const detectShortages = async (purchaseId: number, currentItems: any[]): Promise<{ shortages: DetectedShortage[]; shipmentInfo: { id: number; serial: string } | null }> => {
    const r = await trackingAPI.shipmentItemsForPurchase(purchaseId);
    if (!r.success || !r.shipment) return { shortages: [], shipmentInfo: null };
    const shipmentItems = r.items || [];
    if (shipmentItems.length === 0) return { shortages: [], shipmentInfo: null };
    const totalShipment = shipmentItems.reduce((sum: number, it: any) => sum + Number(it.quantity || 0), 0);
    const totalPurchase = currentItems.reduce((sum: number, it: any) => sum + Number(it.quantity || 0), 0);
    const detected: DetectedShortage[] = [];
    if (totalShipment - totalPurchase > 0.001) {
      const firstItem = shipmentItems[0];
      detected.push({
        shipment_item_id: firstItem.id,
        product_id: firstItem.product_id,
        product_name: firstItem.product_name,
        original_qty: totalShipment,
        actual_qty: totalPurchase,
        shortage_qty: totalShipment - totalPurchase,
      });
    }
    return {
      shortages: detected,
      shipmentInfo: r.shipment ? { id: r.shipment.id, serial: r.shipment.serial } : null
    };
  };

  const handleEdit = async () => {
    if (!editItem.vendor_id) { showToast("فروشنده الزامی است", false); return; }
    if (!editItem.items || editItem.items.length === 0) { showToast("حداقل یک آیتم اضافه کنید", false); return; }
    const detection = await detectShortages(editItem.id, editItem.items);
    if (detection.shortages.length > 0 && detection.shipmentInfo) {
      setShortageWarning({
        shortages: detection.shortages,
        shipmentInfo: detection.shipmentInfo,
        purchaseId: editItem.id,
      });
      setShortageDescription("");
      return;
    }
    await saveEditedPurchase();
  };

  const saveEditedPurchase = async () => {
    const vendor = vendors.find((v: any) => String(v.id) === editItem.vendor_id);
    const res = await journalAPI.updatePurchase({
      id: editItem.id,
      date: editItem.date,
      vendor_id: parseInt(editItem.vendor_id),
      vendor_name: `${vendor?.first_name || ""} ${vendor?.last_name || ""} ${vendor?.company_name || ""}`.trim(),
      vendor_currency: vendor?.currency || editItem.vendor_currency,
      discount: parseFloat(editItem.discount || "0"),
      items: editItem.items.map((i: any) => {
        const isService = String(i.product_id).startsWith("svc_");
        return {
          product_id:     isService ? parseInt(String(i.product_id).replace("svc_", "")) : (i.product_id ? parseInt(i.product_id) : null),
          product_name:   i.product_name,
          unit:           i.unit,
          quantity:       i.quantity,
          warehouse_id:   null,
          warehouse_name: "",
          unit_price:     i.unit_price,
          total_price:    i.total_price,
        };
      }),
      notes: editItem.notes,
      attachment: editItem.attachment,
    });
    if (res.success) {
      showToast("خرید ویرایش شد");
      setEditItem(null);
      setShortageWarning(null);
      setShortageDescription("");
      loadList();
    } else showToast(res.message || "خطا", false);
  };

  const confirmShortageAndSave = async () => {
    if (!shortageWarning) return;
    if (!shortageDescription.trim()) {
      showToast("توضیح کسری الزامی است", false);
      return;
    }
    for (const sh of shortageWarning.shortages) {
      await trackingAPI.shortageAdd({
        shipment_id: shortageWarning.shipmentInfo.id,
        shipment_item_id: sh.shipment_item_id,
        product_id: sh.product_id,
        product_name: sh.product_name,
        original_qty: sh.original_qty,
        actual_qty: sh.actual_qty,
        shortage_qty: sh.shortage_qty,
        description: shortageDescription,
        purchase_id: shortageWarning.purchaseId,
      });
    }
    await saveEditedPurchase();
  };

  const handleDelete = async (id: number, serial: string) => {
    if (!window.confirm(`حذف خرید ${serial}؟`)) return;
    const res = await journalAPI.deletePurchase(id);
    if (res.success) { showToast("خرید حذف شد"); loadList(); }
    else showToast(res.message || "خطا", false);
  };

  const filtersLabel = describeFilter(filter, { counterpartyLabel: "فروشنده", counterparty: vendors.map((v: any) => ({ value: String(v.id), label: `${v.first_name || ""} ${v.last_name || ""} ${v.company_name || ""}`.trim() })) });

  return (
    <div style={{ fontFamily: "'Vazirmatn','Segoe UI',sans-serif" }}>

      {toast.text && <Toast text={toast.text} ok={toast.ok} />}

      {/* Toolbar — فیلتر + صفحه‌بندی سمت سرور */}
      <JournalFilter
        config={{
          searchPlaceholder: "جستجو در خریدها... (شماره سند، نام فروشنده، توضیحات، شماره موتر)",
          showDateRange: true,
          showAmountRange: false,
          showCurrency: false,
          showCounterparty: true,
          counterpartyLabel: "فروشنده",
          counterpartyOptions: vendors.map((v: any) => ({
            value: String(v.id),
            label: `${v.first_name || ""} ${v.last_name || ""} ${v.company_name || ""}`.trim(),
          })),
        }}
        values={filter}
        onChange={setFilter}
        resultCount={list.length}
        totalCount={totalAll}
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        T={T}
        dark={dark}
        onReport={() => setShowReport(true)}
        reportLabel="گزارش PDF / Excel خریدات"
      />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 10, flexWrap: "wrap" }}>
        <StatusTabs
          value={statusFilter}
          onChange={setStatusFilter}
          totalCount={pendingCount + confirmedCount}
          pendingCount={pendingCount}
          T={T}
          dark={dark}
        />
        {canAdd && (
          <button onClick={() => { setShowForm(true); if (items.length === 0) addItem(); }} style={addBtnSt("#f59e0b")}>
            <Plus size={15} /> ثبت خرید جدید
          </button>
        )}
      </div>

      {/* List */}
      {loading ? <Loading T={T} /> : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: "68vh", overflowY: "auto" }}>
          {list.length === 0 && <Empty T={T} text="هیچ خریدی یافت نشد" />}
          {list.length > 0 && (
            <div style={{
              display: "grid",
              gridTemplateColumns: "130px 90px 220px 1fr 110px 75px 1.2fr 160px 200px",
              gap: 12,
              padding: "10px 16px",
              borderRadius: 10,
              background: dark ? "#1e293b" : "#f1f5f9",
              position: "sticky" as const, top: 0, zIndex: 2,
              border: `1px solid ${T.cardBorder}`,
              fontSize: 11,
              fontWeight: 700,
              color: T.subtext,
            }}>
              <div>شماره سند</div>
              <div>نوع</div>
              <div>موتر / بارنامه</div>
              <div>فروشنده</div>
              <div>تاریخ</div>
              <div style={{ textAlign: "center" as const }}>آیتم‌ها</div>
              <div>شرح</div>
              <div style={{ textAlign: "left" as const }}>مبلغ خالص</div>
              <div style={{ textAlign: "center" as const }}>عملیات</div>
            </div>
          )}
          {list.map((p: any) => {
            const isPending = (p.status || "confirmed") === "pending";
            const foreign = p.purchase_type === "foreign";
            return (
            <div key={p.id} style={{
              ...rowSt(dark, T),
              ...(isPending ? pendingRowStyle(dark) : {}),
              display: "grid",
              gridTemplateColumns: "130px 90px 220px 1fr 110px 75px 1.2fr 160px 200px",
              gap: 12,
              alignItems: "center",
            }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
                <span style={badgeSt("#f59e0b")}>{p.serial}</span>
                {isPending && <PendingBadge />}
                {p.attachment && <a href={fileUrl(p.attachment)} target="_blank" rel="noreferrer" style={{ fontSize: 10.5, color: "#3b82f6", textDecoration: "none" }}>📎 ضمیمه</a>}
              </div>

              {/* 🆕 نوع خرید */}
              <div>
                {foreign ? (
                  <span style={{ ...badgeSt("#8b5cf6"), display: "inline-flex", alignItems: "center", gap: 4 }}>
                    <Ship size={11} /> خارجی
                  </span>
                ) : (
                  <span style={{ ...badgeSt("#10b981"), display: "inline-flex", alignItems: "center", gap: 4 }}>
                    داخلی
                  </span>
                )}
                {foreign && p.transit_status === "pending" && (
                  <div style={{ fontSize: 10, color: "#8b5cf6", marginTop: 3 }}>در انتظار ترانزیت</div>
                )}
                {foreign && p.transit_status === "in_transit" && (
                  <div style={{ fontSize: 10, color: "#f59e0b", marginTop: 3, fontWeight: 700 }}>🚛 در مسیر (ترانزیت)</div>
                )}
                {foreign && p.transit_status === "completed" && (
                  <div style={{ fontSize: 10, color: "#10b981", marginTop: 3, fontWeight: 700 }}>✓ ترانزیت تکمیل شد</div>
                )}
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
                {p.truck_plate && (
                  <span style={{
                    padding: "3px 10px", borderRadius: 7,
                    background: "rgba(59,130,246,0.1)", color: "#3b82f6",
                    fontSize: 11, fontWeight: 700, fontFamily: "monospace",
                    whiteSpace: "nowrap" as const,
                  }}>
                    🚚 {p.truck_plate}
                  </span>
                )}
                {p.waybill_number && (
                  <span style={{
                    padding: "3px 10px", borderRadius: 7,
                    background: "rgba(14,165,233,0.1)", color: "#0ea5e9",
                    fontSize: 11, fontWeight: 700, fontFamily: "monospace",
                    whiteSpace: "nowrap" as const,
                  }}>
                    📄 {p.waybill_number}
                  </span>
                )}
              </div>

              <div style={{ fontWeight: 700, color: T.text, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis" }}>
                {p.vendor_name}
              </div>

              <div>
                <DualDate date={p.date} fontSize={12} color={T.subtext} />
              </div>

              <div style={{ fontSize: 12, color: T.subtext, textAlign: "center" as const }}>
                {p.items?.length || 0} آیتم
              </div>

              <div style={{ fontSize: 11, color: T.subtext, fontStyle: "italic", lineHeight: 1.5, wordBreak: "break-word" as const }}>
                {p.notes || "—"}
              </div>

              <div style={{ fontWeight: 800, color: "#f59e0b", fontSize: 14, textAlign: "left" as const, direction: "ltr" as const }}>
                {fmt(p.net_amount)} {p.vendor_currency}
              </div>

              <div style={{ display: "flex", gap: 6, alignItems: "center", justifyContent: "flex-end" }}>
                <button onClick={() => setViewItem(p)} title="مشاهده" style={iconBtnSt("#8b5cf6")}><Eye size={13} /></button>
                {isPending && canEdit && (
                  <ConfirmIconButton onClick={() => handleConfirm(p.id, p.serial)} />
                )}
                {!isPending && canEdit && (
                  <RevertIconButton onClick={() => handleRevert(p.id, p.serial)} />
                )}
                {canEdit && (
                  <button onClick={() => openEdit(p)} title="ویرایش" style={iconBtnSt("#3b82f6")}><Edit2 size={13} /></button>
                )}
                {canDelete && (
                  <button onClick={() => handleDelete(p.id, p.serial)} title="حذف" style={iconBtnSt("#ef4444")}><Trash2 size={13} /></button>
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* فرم ثبت خرید */}
      {showForm && (
        <Modal title="ثبت خرید جدید" onClose={() => setShowForm(false)} dark={dark} T={T}>

          {/* 🆕 نوع خرید: داخلی / خارجی */}
          <div style={{ display: "flex", gap: 10, marginBottom: 18 }}>
            {(["domestic", "foreign"] as const).map(t => (
              <button
                key={t}
                onClick={() => setForm(f => ({ ...f, purchase_type: t }))}
                style={{
                  flex: 1, padding: "12px 16px", borderRadius: 12, cursor: "pointer",
                  border: `2px solid ${form.purchase_type === t ? (t === "foreign" ? "#8b5cf6" : "#10b981") : T.cardBorder}`,
                  background: form.purchase_type === t
                    ? (t === "foreign" ? "rgba(139,92,246,0.1)" : "rgba(16,185,129,0.1)")
                    : "transparent",
                  color: form.purchase_type === t ? (t === "foreign" ? "#8b5cf6" : "#10b981") : T.subtext,
                  fontWeight: 800, fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
                }}
              >
                {t === "foreign" ? <Ship size={16} /> : <Truck size={16} />}
                {t === "foreign" ? "خرید خارجی" : "خرید داخلی"}
              </button>
            ))}
          </div>
          {isForeign && (
            <div style={{
              background: dark ? "rgba(139,92,246,0.08)" : "#f5f3ff", borderRadius: 10,
              padding: "10px 14px", marginBottom: 16, fontSize: 12, color: "#8b5cf6", lineHeight: 1.7,
            }}>
              ℹ️ در خرید خارجی فقط جنس انتخاب می‌شود (بدون گدام). بیلانس فروشنده مثل معمول به‌روز می‌شود، اما
              موجودی هیچ گدامی تغییر نمی‌کند — این خرید به‌عنوان «در انتظار ترانزیت/ترانشیپ» ثبت می‌شود و موجودی فقط
              در مرحله‌ی نهایی ترانزیت (رسید گدام) اضافه خواهد شد.
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 20 }}>
            <div>
              <Lbl T={T}>فروشنده *</Lbl>
              <SearchableSelect
                options={vendors.map((v: any) => ({ value: String(v.id), label: `${v.first_name || ""} ${v.last_name || ""} ${v.company_name || ""}`.trim() + ` (${v.currency})` }))}
                value={form.vendor_id}
                onChange={v => {
                  const vn = vendors.find((x: any) => String(x.id) === v);
                  setForm(f => ({ ...f, vendor_id: v, vendor_currency: vn?.currency || "USD" }));
                }}
                placeholder="انتخاب فروشنده"
              />
            </div>
            <div>
              <Lbl T={T}>تاریخ</Lbl>
              <PersianDatePicker value={form.date} onChange={v => setForm(f => ({ ...f, date: v }))} />
            </div>
            <div>
              <Lbl T={T}>تخفیف</Lbl>
              <input value={form.discount} onChange={e => setForm(f => ({ ...f, discount: e.target.value }))}
                placeholder="0" style={inputSt(T)} />
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <strong style={{ fontSize: 13, color: T.text }}>آیتم‌های خرید</strong>
              <button onClick={addItem} style={addBtnSt("#f59e0b", true)}><Plus size={13} /> افزودن آیتم</button>
            </div>
            {items.map((item, i) => {
              return (
              <div key={i} style={{
                display: "grid",
                gridTemplateColumns: "2.2fr 90px 100px 110px 36px",
                gap: 8, marginBottom: 10, alignItems: "end", padding: "12px", borderRadius: 10,
                background: dark ? "rgba(255,255,255,0.02)" : "#f8fafc", border: `1px solid ${T.border}`,
              }}>
                <div>
                  {i === 0 && <Lbl T={T}>محصول / خدمت</Lbl>}
                  <SearchableSelect
                    options={productOnlyOptions.map(o => ({ value: o.value, label: o.label }))}
                    value={item.product_id}
                    onChange={v => updateItemProduct(i, v)}
                    placeholder="جستجوی جنس یا خدمت..."
                  />
                  {item.product_id && productOnlyOptions.find(o => o.value === item.product_id)?.category_name && (
                    <div style={{ marginTop: 4, fontSize: 11, color: "#8b5cf6", fontWeight: 700 }}>🏷 دسته‌بندی: {productOnlyOptions.find(o => o.value === item.product_id)?.category_name}</div>
                  )}
                </div>

                <div>
                  {i === 0 && <Lbl T={T}>واحد</Lbl>}
                  <div style={{ padding: "10px 12px", borderRadius: 10, background: dark ? "rgba(255,255,255,0.04)" : "#fff", border: `1.5px solid ${T.border}`, color: T.text, fontSize: 13, minHeight: 42 }}>
                    {item.unit || "—"}
                  </div>
                </div>
                <div>
                  {i === 0 && <Lbl T={T}>تعداد</Lbl>}
                  <input type="number" value={item.quantity}
                    onChange={e => updateItem(i, "quantity", Number(e.target.value))}
                    style={inputSt(T)} />
                </div>
                <div>
                  {i === 0 && <Lbl T={T}>قیمت واحد</Lbl>}
                  <input type="number" value={item.unit_price}
                    onChange={e => updateItem(i, "unit_price", Number(e.target.value))}
                    style={inputSt(T)} />
                </div>
                <div style={{ marginTop: i === 0 ? 22 : 0 }}>
                  <button onClick={() => removeItem(i)} style={{ ...iconBtnSt("#ef4444"), width: 36, height: 36 }}>
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
              );
            })}
          </div>

          <div style={{ background: dark ? "rgba(245,158,11,0.08)" : "#fffbeb", borderRadius: 12, padding: "14px 18px", marginBottom: 16, display: "flex", gap: 28, flexWrap: "wrap" }}>
            <div><span style={{ fontSize: 12, color: T.subtext }}>جمع کل: </span><strong style={{ color: T.text }}>{fmt(totalAmount)}</strong></div>
            <div><span style={{ fontSize: 12, color: T.subtext }}>تخفیف: </span><strong style={{ color: "#ef4444" }}>{fmt(parseFloat(form.discount || "0"))}</strong></div>
            <div><span style={{ fontSize: 12, color: T.subtext }}>خالص: </span><strong style={{ color: "#f59e0b", fontSize: 16 }}>{fmt(netAmount)} {form.vendor_currency}</strong></div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 20 }}>
            <div>
              <Lbl T={T}>توضیحات</Lbl>
              <input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="توضیحات..." style={inputSt(T)} />
            </div>
            <div>
              <Lbl T={T}>ضمیمه</Lbl>
              <AttachmentField
                value={form.attachment}
                fileName={form.attachment_file_name}
                onChange={(path, name) => setForm(f => ({ ...f, attachment: path, attachment_file_name: name }))}
                uploadFn={(file) => journalAPI.uploadPurchaseAttachment(file)}
                T={T} dark={dark}
              />
            </div>
          </div>
          <div style={{ fontSize: 11.5, color: T.subtext, marginBottom: 20, lineHeight: 1.8 }}>
            ℹ️ شماره موتر، شماره بارنامه و توزیع بار بین موترها/گدام‌ها، در مرحله‌ی «توزیع بار» (بعد از تأیید این خرید) ثبت می‌شود.
          </div>

          <div style={{
            display: "flex", gap: 12, justifyContent: "space-between", alignItems: "center",
            marginTop: 24, paddingTop: 20, borderTop: `1px solid ${T.cardBorder}`,
            flexWrap: "wrap"
          }}>
            <PendingHint T={T} />
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <BtnCancel onClick={() => setShowForm(false)} T={T} />
              <PendingSubmitButton
                onClick={() => submit(handleSubmitPending)}
                loading={submitting}
                label="ذخیره خرید در انتظار"
              />
              {/* 🆕 قبل از ثبت نهایی، دیالوگ کرایه موتر و پیش‌نمایش باز می‌شود */}
              <ConfirmSubmitButton
                onClick={openPreview}
                loading={submitting}
                color="#f59e0b"
                label="ثبت خرید"
              />
            </div>
          </div>
        </Modal>
      )}

      {/* 🆕 پیش‌نمایش قبل از ثبت نهایی */}
      {showPreview && (
        <Modal title="پیش‌نمایش خرید — قبل از ثبت نهایی" onClose={() => setShowPreview(false)} dark={dark} T={T}>
          <div id="print-purchase-preview">
            <div style={{ display: "flex", gap: 24, marginBottom: 16, flexWrap: "wrap" }}>
              <span style={{ fontSize: 12, color: T.subtext }}>فروشنده: <strong style={{ color: T.text }}>{selectedVendor ? `${selectedVendor.first_name || ""} ${selectedVendor.last_name || ""} ${selectedVendor.company_name || ""}`.trim() : "—"}</strong></span>
              <span style={{ fontSize: 12, color: T.subtext }}>تاریخ: <strong style={{ color: T.text }}>{gregorianToJalaliStr(form.date)}</strong></span>
              <span style={{ fontSize: 12, color: T.subtext }}>نوع: <strong style={{ color: isForeign ? "#8b5cf6" : "#10b981" }}>{isForeign ? "خارجی" : "داخلی"}</strong></span>
            </div>

            <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 20 }}>
              <thead>
                <tr style={{ background: dark ? "rgba(255,255,255,0.05)" : "#f8fafc" }}>
                  {["محصول", "واحد", "تعداد", "قیمت واحد", "مجموع"].map(h => (
                    <th key={h} style={{ padding: "10px 12px", textAlign: "right", fontSize: 12, fontWeight: 700, color: T.subtext, borderBottom: `1px solid ${T.cardBorder}` }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${T.cardBorder}` }}>
                    <td style={{ padding: "10px 12px", fontSize: 13, color: T.text }}>{it.product_name}{it.product_category_name ? <span style={{ marginRight: 8, fontSize: 10.5, color: "#8b5cf6", fontWeight: 700 }}>🏷 {it.product_category_name}</span> : null}</td>
                    <td style={{ padding: "10px 12px", fontSize: 13, color: T.text }}>{it.unit}</td>
                    <td style={{ padding: "10px 12px", fontSize: 13, color: T.text }}>{it.quantity}</td>
                    <td style={{ padding: "10px 12px", fontSize: 13, color: T.text }}>{fmt(it.unit_price)}</td>
                    <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 700, color: "#f59e0b" }}>{fmt(it.total_price)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ background: dark ? "rgba(16,185,129,0.08)" : "#ecfdf5", borderRadius: 12, padding: 16, marginBottom: 12 }}>
              <strong style={{ fontSize: 13, color: "#10b981", display: "block", marginBottom: 8 }}>اثر روی بیلانس فروشنده</strong>
              <div style={{ fontSize: 12, color: T.text, lineHeight: 1.8 }}>
                مبلغ خالص <strong>{fmt(netAmount)} {form.vendor_currency}</strong> به بدهی فروشنده افزوده می‌شود.
              </div>
            </div>

            <div style={{ background: dark ? "rgba(139,92,246,0.08)" : "#f5f3ff", borderRadius: 12, padding: 16 }}>
              <strong style={{ fontSize: 13, color: "#8b5cf6", display: "block", marginBottom: 8 }}>موجودی گدام</strong>
              <div style={{ fontSize: 12, color: T.text, lineHeight: 1.8 }}>
                {isForeign
                  ? "این خرید خارجی است — پس از ثبت، در انتظار مرحله‌ی ترانزیت/ترانشیپ خواهد بود؛ موجودی گدام در پایان همان مرحله اضافه می‌شود."
                  : "این خرید فقط مالی ثبت می‌شود — بعد از تأیید، از «توزیع بار» (در منوی مدیریت گدام) بین موترها تقسیم و سپس در «رسید گدام» وارد موجودی می‌شود."}
              </div>
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 20, paddingTop: 16, borderTop: `1px solid ${T.cardBorder}` }}>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => {
                const w = window.open("", "_blank");
                if (w) {
                  w.document.write(`<html dir="rtl"><head><title>پیش‌نمایش خرید</title><style>body{font-family:'Vazirmatn',sans-serif;padding:20px;direction:rtl}table{width:100%;border-collapse:collapse}th,td{padding:8px 12px;border:1px solid #ddd;text-align:right;font-size:12px}th{background:#f8fafc;font-weight:700}</style></head><body>${document.getElementById("print-purchase-preview")?.innerHTML || ""}</body></html>`);
                  w.document.close(); w.print();
                }
              }} style={addBtnSt("#8b5cf6", true)}>
                <Printer size={13} /> چاپ
              </button>
              <button onClick={() => {
                const w = window.open("", "_blank");
                if (w) {
                  w.document.write(`<html dir="rtl"><head><title>پیش‌نمایش خرید</title><style>body{font-family:'Vazirmatn',sans-serif;padding:20px;direction:rtl}table{width:100%;border-collapse:collapse}th,td{padding:8px 12px;border:1px solid #ddd;text-align:right;font-size:12px}th{background:#f8fafc;font-weight:700}</style></head><body>${document.getElementById("print-purchase-preview")?.innerHTML || ""}</body></html>`);
                  w.document.close(); w.print();
                }
              }} style={addBtnSt("#64748b", true)}>
                ذخیره PDF
              </button>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <BtnCancel onClick={() => setShowPreview(false)} T={T} />
              <ConfirmSubmitButton
                onClick={() => submit(handleSubmit)}
                loading={submitting}
                color="#f59e0b"
                label="تأیید و ثبت نهایی"
              />
            </div>
          </div>
        </Modal>
      )}

      {/* فرم ویرایش خرید */}
      {editItem && (
        <Modal title={`ویرایش خرید${editItem.status==="pending" ? " (در انتظار)" : ""}`} onClose={() => setEditItem(null)} dark={dark} T={T}>
          {editItem.purchase_type === "foreign" && (
            <div style={{
              background: dark ? "rgba(139,92,246,0.08)" : "#f5f3ff", borderRadius: 10,
              padding: "10px 14px", marginBottom: 16, fontSize: 12, color: "#8b5cf6",
            }}>
              ℹ️ این خرید «خارجی» است — نوع خرید در ویرایش قابل تغییر نیست و گدام برای آیتم‌هایش اعمال نمی‌شود.
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 20 }}>
            <div>
              <Lbl T={T}>فروشنده *</Lbl>
              <SearchableSelect
                options={vendors.map((v: any) => ({ value: String(v.id), label: `${v.first_name || ""} ${v.last_name || ""} ${v.company_name || ""}`.trim() + ` (${v.currency})` }))}
                value={editItem.vendor_id}
                onChange={v => { const vn = vendors.find((x: any) => String(x.id) === v); setEditItem((f: any) => ({ ...f, vendor_id: v, vendor_currency: vn?.currency || "USD" })); }}
                placeholder="انتخاب فروشنده"
              />
            </div>
            <div>
              <Lbl T={T}>تاریخ</Lbl>
              <PersianDatePicker value={editItem.date} onChange={v => setEditItem((f: any) => ({ ...f, date: v }))} />
            </div>
            <div>
              <Lbl T={T}>تخفیف</Lbl>
              <input value={editItem.discount} onChange={e => setEditItem((f: any) => ({ ...f, discount: e.target.value }))} placeholder="0" style={inputSt(T)} />
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <strong style={{ fontSize: 13, color: T.text }}>آیتم‌ها</strong>
              <button onClick={() => setEditItem((f: any) => ({ ...f, items: [...f.items, { product_id: "", product_name: "", unit: "", quantity: 1, warehouse_id: "", warehouse_name: "", unit_price: 0, total_price: 0 }] }))} style={addBtnSt("#f59e0b", true)}><Plus size={13} /> افزودن آیتم</button>
            </div>
            {editItem.items.map((item: any, i: number) => (
              <div key={i} style={{
                display: "grid",
                gridTemplateColumns: "2.2fr 90px 100px 110px 36px",
                gap: 8, marginBottom: 10, alignItems: "end", padding: "12px", borderRadius: 10,
                background: dark ? "rgba(255,255,255,0.02)" : "#f8fafc", border: `1px solid ${T.border}`,
              }}>
                <div>
                  {i === 0 && <Lbl T={T}>محصول / خدمت</Lbl>}
                  <SearchableSelect
                    options={productOnlyOptions.map(o => ({ value: o.value, label: o.label }))}
                    value={item.product_id}
                    onChange={v => {
                      const opt = productOnlyOptions.find(o => o.value === v);
                      setEditItem((f: any) => ({ ...f, items: f.items.map((it: any, idx: number) => idx === i ? {
                        ...it, product_id: v, product_name: opt?.product_name || "", unit: opt?.unit || "",
                        total_price: it.quantity * it.unit_price,
                      } : it) }));
                    }}
                    placeholder="جستجوی جنس یا خدمت..."
                  />
                </div>
                <div>
                  {i === 0 && <Lbl T={T}>واحد</Lbl>}
                  <div style={{ padding: "10px 12px", borderRadius: 10, background: dark ? "rgba(255,255,255,0.04)" : "#fff", border: `1.5px solid ${T.border}`, color: T.text, fontSize: 13, minHeight: 42 }}>{item.unit || "—"}</div>
                </div>
                <div>
                  {i === 0 && <Lbl T={T}>تعداد</Lbl>}
                  <input type="number" value={item.quantity} onChange={e => setEditItem((f: any) => ({ ...f, items: f.items.map((it: any, idx: number) => idx === i ? { ...it, quantity: Number(e.target.value), total_price: Number(e.target.value) * it.unit_price } : it) }))} style={inputSt(T)} />
                </div>
                <div>
                  {i === 0 && <Lbl T={T}>قیمت واحد</Lbl>}
                  <input type="number" value={item.unit_price} onChange={e => setEditItem((f: any) => ({ ...f, items: f.items.map((it: any, idx: number) => idx === i ? { ...it, unit_price: Number(e.target.value), total_price: it.quantity * Number(e.target.value) } : it) }))} style={inputSt(T)} />
                </div>
                <div style={{ marginTop: i === 0 ? 22 : 0 }}>
                  <button onClick={() => setEditItem((f: any) => ({ ...f, items: f.items.filter((_: any, idx: number) => idx !== i) }))} style={{ ...iconBtnSt("#ef4444"), width: 36, height: 36 }}><Trash2 size={13} /></button>
                </div>
              </div>
            ))}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 20 }}>
            <div><Lbl T={T}>توضیحات</Lbl><input value={editItem.notes} onChange={e => setEditItem((f: any) => ({ ...f, notes: e.target.value }))} placeholder="توضیحات..." style={inputSt(T)} /></div>
            <div>
              <Lbl T={T}>ضمیمه</Lbl>
              <AttachmentField
                value={editItem.attachment}
                fileName={editItem.attachment_file_name}
                onChange={(path, name) => setEditItem((f: any) => ({ ...f, attachment: path, attachment_file_name: name }))}
                uploadFn={(file) => journalAPI.uploadPurchaseAttachment(file)}
                T={T} dark={dark}
              />
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <BtnCancel onClick={() => setEditItem(null)} T={T} />
            <BtnSave onClick={() => submit(handleEdit)} loading={submitting} color="#3b82f6" label="ذخیره ویرایش" />
          </div>
        </Modal>
      )}

      {/* 🆕 مودال هشدار کسری */}
      {shortageWarning && (
        <Modal title="⚠️ هشدار کسری" onClose={() => setShortageWarning(null)} dark={dark} T={T}>
          <div style={{
            background: dark ? "rgba(239,68,68,0.08)" : "#fef2f2", borderRadius: 12,
            padding: 16, marginBottom: 16, display: "flex", alignItems: "flex-start", gap: 10,
          }}>
            <AlertTriangle size={20} color="#ef4444" style={{ flexShrink: 0, marginTop: 2 }} />
            <div style={{ fontSize: 13, color: T.text, lineHeight: 1.7 }}>
              مقدار خرید کمتر از مقدار بار مرتبط با آن است. این کسری در گزارش کسری بارها ثبت خواهد شد.
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <div style={{
              display: "grid", gridTemplateColumns: "1fr 100px 100px 100px", gap: 8,
              padding: "8px 12px", fontSize: 11, fontWeight: 700, color: T.subtext,
            }}>
              <span>جنس</span><span style={{ textAlign: "center" }}>مقدار بار</span><span style={{ textAlign: "center" }}>مقدار خرید</span><span style={{ textAlign: "center" }}>کسری</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {shortageWarning.shortages.map((sh, i) => (
                <div key={i} style={{
                  display: "grid", gridTemplateColumns: "1fr 100px 100px 100px", gap: 8,
                  padding: "10px 12px", borderRadius: 8,
                  background: dark ? "rgba(255,255,255,0.03)" : "#f8fafc",
                  fontSize: 13, alignItems: "center",
                }}>
                  <span style={{ color: T.text }}>{sh.product_name}</span>
                  <span style={{ textAlign: "center", color: T.text }}>{fmt(sh.original_qty)}</span>
                  <span style={{ textAlign: "center", color: T.text }}>{fmt(sh.actual_qty)}</span>
                  <span style={{ textAlign: "center", color: "#ef4444", fontWeight: 800, fontSize: 13 }}>
                    {fmt(sh.shortage_qty)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <Lbl T={T}>توضیح علت کسری *</Lbl>
            <textarea
              value={shortageDescription}
              onChange={e => setShortageDescription(e.target.value)}
              placeholder="مثلاً: کیسه‌های پاره، کم‌فروشی در گمرک، ضایعات در حمل، ..."
              rows={3}
              style={{
                ...inputSt(T),
                resize: "vertical",
                minHeight: 80,
              }}
            />
            <div style={{ fontSize: 11, color: T.subtext, marginTop: 6 }}>
              ℹ️ این توضیح در گزارش کسری بارها ثبت می‌شود تا قابل پیگیری باشد.
            </div>
          </div>

          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", paddingTop: 14, borderTop: `1px solid ${T.cardBorder}` }}>
            <BtnCancel onClick={() => { setShortageWarning(null); setShortageDescription(""); }} T={T} />
            <button
              onClick={() => submit(confirmShortageAndSave)}
              disabled={submitting || !shortageDescription.trim()}
              style={{
                padding: "10px 24px", borderRadius: 10, border: "none",
                background: (!shortageDescription.trim() || submitting) ? "#9ca3af" : "linear-gradient(135deg,#ef4444,#dc2626)",
                color: "#fff", cursor: (!shortageDescription.trim() || submitting) ? "not-allowed" : "pointer",
                fontSize: 13, fontWeight: 700, fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
                display: "flex", alignItems: "center", gap: 6,
              }}
            >
              <AlertTriangle size={14} /> {submitting ? "در حال ثبت..." : "تأیید کسری و ذخیره"}
            </button>
          </div>
        </Modal>
      )}

      {/* مشاهده جزئیات + چاپ */}
      {viewItem && (
        <Modal title={`جزئیات خرید: ${viewItem.serial}`} onClose={() => setViewItem(null)} dark={dark} T={T}>
          <div id="print-purchase">
            <div style={{ display: "flex", gap: 24, marginBottom: 16, flexWrap: "wrap" }}>
              <span style={{ fontSize: 12, color: T.subtext }}>فروشنده: <strong style={{ color: T.text }}>{viewItem.vendor_name}</strong></span>
              <span style={{ fontSize: 12, color: T.subtext, display: "inline-flex", alignItems: "center", gap: 6 }}>تاریخ: <DualDate date={viewItem.date} fontSize={12} color={T.text} inline /></span>
              <span style={{ fontSize: 12, color: T.subtext }}>نوع: <strong style={{ color: viewItem.purchase_type === "foreign" ? "#8b5cf6" : "#10b981" }}>{viewItem.purchase_type === "foreign" ? "خارجی" : "داخلی"}</strong></span>
              <span style={{ fontSize: 12, color: T.subtext }}>خالص: <strong style={{ color: "#f59e0b" }}>{fmt(viewItem.net_amount)} {viewItem.vendor_currency}</strong></span>
              {viewItem.notes && <span style={{ fontSize: 12, color: T.subtext }}>توضیح: <strong style={{ color: T.text }}>{viewItem.notes}</strong></span>}
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: dark ? "rgba(255,255,255,0.05)" : "#f8fafc" }}>
                  {["محصول", "واحد", "تعداد", "گدام", "قیمت واحد", "مجموع"].map(h => (
                    <th key={h} style={{ padding: "10px 12px", textAlign: "right", fontSize: 12, fontWeight: 700, color: T.subtext, borderBottom: `1px solid ${T.cardBorder}` }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {viewItem.items?.map((it: any, i: number) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${T.cardBorder}` }}>
                    <td style={{ padding: "10px 12px", fontSize: 13, color: T.text }}>{it.product_name}{it.product_category_name ? <span style={{ marginRight: 8, fontSize: 10.5, color: "#8b5cf6", fontWeight: 700 }}>🏷 {it.product_category_name}</span> : null}</td>
                    <td style={{ padding: "10px 12px", fontSize: 13, color: T.text }}>{it.unit}</td>
                    <td style={{ padding: "10px 12px", fontSize: 13, color: T.text }}>{it.quantity}</td>
                    <td style={{ padding: "10px 12px", fontSize: 13, color: T.text }}>{it.warehouse_name || "—"}</td>
                    <td style={{ padding: "10px 12px", fontSize: 13, color: T.text }}>{fmt(it.unit_price)}</td>
                    <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 700, color: "#f59e0b" }}>{fmt(it.total_price)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16 }}>
            {viewItem.attachment ? (
              <a href={viewItem.attachment} target="_blank" rel="noreferrer" style={{
                display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#3b82f6", textDecoration: "none",
              }}>
                <Paperclip size={14} /> دانلود ضمیمه
              </a>
            ) : <span />}
            <button onClick={() => {
              const w = window.open("", "_blank");
              if (w) {
                w.document.write(`<html dir="rtl"><head><title>${viewItem.serial}</title><style>body{font-family:'Vazirmatn',sans-serif;padding:20px;direction:rtl}table{width:100%;border-collapse:collapse}th,td{padding:8px 12px;border:1px solid #ddd;text-align:right;font-size:12px}th{background:#f8fafc;font-weight:700}</style></head><body>${document.getElementById("print-purchase")?.innerHTML || ""}</body></html>`);
                w.document.close(); w.print();
              }
            }} style={addBtnSt("#8b5cf6")}>
              <Printer size={14} /> چاپ سند
            </button>
          </div>
        </Modal>
      )}
      {showReport && (
        <TxnReportModal type="purchases" title="خریدات" filter={filter} filtersLabel={filtersLabel} onClose={() => setShowReport(false)} T={T} dark={dark} />
      )}
    </div>
  );
}

// ---- shared helpers ----
export const inputSt = (T: any, extra?: any): React.CSSProperties => ({
  width: "100%", padding: "10px 14px", borderRadius: 10,
  border: `1.5px solid ${T.border}`, background: T.input,
  color: T.text, fontSize: 13, fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
  boxSizing: "border-box", outline: "none", ...extra,
});
export const rowSt = (dark: boolean, T: any): React.CSSProperties => ({
  borderRadius: 12, padding: "14px 16px",
  background: dark ? "rgba(255,255,255,0.03)" : "#fff",
  border: `1px solid ${T.cardBorder}`,
});
export const badgeSt = (color: string): React.CSSProperties => ({
  padding: "3px 10px", borderRadius: 8,
  background: `${color}18`, color, fontSize: 12, fontWeight: 700, fontFamily: "monospace",
});
export const addBtnSt = (color: string, small?: boolean): React.CSSProperties => ({
  display: "flex", alignItems: "center", gap: 6,
  padding: small ? "8px 14px" : "10px 20px", borderRadius: 10, border: "none",
  background: `linear-gradient(135deg,${color},${color}bb)`, color: "#fff",
  cursor: "pointer", fontSize: small ? 12 : 13, fontWeight: 700,
  fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
});
export const iconBtnSt = (color: string): React.CSSProperties => ({
  width: 30, height: 30, borderRadius: 8, border: "none",
  background: `${color}18`, color, cursor: "pointer",
  display: "inline-flex", alignItems: "center", justifyContent: "center",
});
export const Lbl = ({ T, children }: any) => (
  <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: T.subtext, marginBottom: 5 }}>
    {children}
  </label>
);
export const BtnCancel = ({ onClick, T }: any) => (
  <button onClick={onClick} style={{
    padding: "10px 20px", borderRadius: 10, border: `1.5px solid ${T.cardBorder}`,
    background: "transparent", color: T.text, cursor: "pointer",
    fontSize: 13, fontWeight: 700, fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
  }}>انصراف</button>
);
export const BtnSave = ({ onClick, loading, color, label }: any) => (
  <button onClick={onClick} disabled={loading} style={{
    padding: "10px 24px", borderRadius: 10, border: "none",
    background: loading ? "#9ca3af" : `linear-gradient(135deg,${color},${color}bb)`,
    color: "#fff", cursor: loading ? "not-allowed" : "pointer",
    fontSize: 13, fontWeight: 700, fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
  }}>{loading ? "در حال ثبت..." : label}</button>
);
export const Toast = ({ text, ok }: any) => (
  <div style={{
    position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)",
    background: ok ? "#10b981" : "#ef4444", color: "#fff",
    padding: "12px 28px", borderRadius: 12, fontSize: 13, fontWeight: 700,
    zIndex: 9999, boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
  }}>{text}</div>
);
export const Loading = ({ T }: any) => (
  <div style={{ textAlign: "center", padding: 60, color: T.subtext }}>در حال بارگذاری...</div>
);
export const Empty = ({ T, text }: any) => (
  <div style={{ textAlign: "center", padding: 40, color: T.subtext, background: "rgba(0,0,0,0.02)", borderRadius: 12 }}>
    {text}
  </div>
);
// بستن با Esc و حرکت Tab از راست به چپ (ترتیب DOM در حالت rtl)؛ اولین فیلد هنگام باز شدن فوکوس می‌گیرد
export function useModalKeys(ref: any, onClose: () => void) {
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") { e.stopPropagation(); onClose(); } };
    document.addEventListener("keydown", h);
    const t = setTimeout(() => {
      const el = ref.current?.querySelector("input:not([type=hidden]):not([disabled]), textarea, select") as HTMLElement | null;
      if (el && !ref.current?.contains(document.activeElement)) el.focus();
    }, 80);
    return () => { document.removeEventListener("keydown", h); clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

export const Modal = ({ title, onClose, dark, T, children, maxWidth }: any) => {
  const boxRef = useRef<HTMLDivElement>(null);
  useModalKeys(boxRef, onClose);
  return (
  <div dir="rtl" style={{
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", backdropFilter: "blur(4px)",
    display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20,
  }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
    <div ref={boxRef} style={{
      background: dark ? "#1e293b" : "#fff", borderRadius: 20, padding: "40px",
      width: "100%", maxWidth: maxWidth || 1100, maxHeight: "90vh", overflow: "auto",
      boxShadow: "0 25px 60px rgba(0,0,0,0.4)",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28 }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: T.text }}>{title}</h3>
        <button onClick={onClose} tabIndex={-1} style={{ background: "none", border: "none", cursor: "pointer", color: T.subtext }}><X size={22} /></button>
      </div>
      {children}
    </div>
  </div>
  );
};
